import update from 'immutability-helper';
import { DeviceCardBuilder } from '@beef/shop-common';

import { FEEDBACK_SORT_BY_RATING } from 'store/ProductDetails/feedbackLocal/constants';
import {
  PRODUCT_KIND_TARIFF,
  PRODUCT_KIND_BILLING,
  BUNDLE_TYPE_PROMOTION,
} from 'store/ProductDetails/bundles/constants';
import { TARIFF_BRAND_NAME } from 'store/ProductDetails/constants';
import { convertHeader2024, convertLegacyHeader } from 'components/Header/data';

/**
 * TODO: remove the entire bundle section when SHOP-1823 is done
 */
const uniteTariffAndBilling = (tariff, billing) =>
  update(tariff, {
    price: {
      value: { $set: tariff.price.value + billing.price.value },
      oldValue: { $set: tariff.price.oldValue + billing.price.oldValue },
    },
  });

const convertBundleExtraProducts = (list) => {
  /**
   * First split products by slug into three groups:
   * tariffs, billings and all the other types
   */
  const {
    [PRODUCT_KIND_TARIFF]: tariffProducts = [],
    [PRODUCT_KIND_BILLING]: billingProducts = [],
    other: otherProducts = [],
  } = list.reduce((hashGroups, item) => {
    let slug;
    switch (item.slug) {
      case PRODUCT_KIND_BILLING:
      case PRODUCT_KIND_TARIFF:
        ({ slug } = item);
        break;
      default:
        slug = 'other';
    }
    if (slug in hashGroups) {
      hashGroups[slug].push(item);
    } else {
      hashGroups[slug] = [item];
    }
    return hashGroups;
  }, {});

  /**
   * Equally split billings between tariffs, to do so we sort billings by price
   * then iterate both tariffs and billings, adding billings to tariff under index
   */
  tariffProducts.sort((a, b) => a.price - b.price);

  /**
   * Main goal is to split billings amongst tariffs, thus we use billing length
   */
  let iterator = billingProducts.length - 1;
  while (iterator >= 0) {
    const tariffIndex = iterator % tariffProducts.length;
    const billingIndex = iterator;
    tariffProducts[tariffIndex] = uniteTariffAndBilling(
      tariffProducts[tariffIndex],
      billingProducts[billingIndex],
    );
    iterator -= 1;
  }

  /**
   * Output devices first, then tariffs, then other elements
   * Billings are ignored, as their price is split amongst tariffs
   */
  return [].concat(otherProducts, tariffProducts);
};

const convertLegacyBundleProduct = (
  { kindFormatted, price, oldPrice, name, imageUrl },
  { htmlDescription },
) => ({
  slug: kindFormatted,
  title: kindFormatted === PRODUCT_KIND_TARIFF ? htmlDescription : name,
  tooltip: null,
  thumbUrl: imageUrl,
  price: {
    value: price,
    oldValue: oldPrice === null ? price : oldPrice,
  },
});

const composeBundleProducts = (productsItem, promotion) => [
  productsItem,
  ...convertBundleExtraProducts(
    promotion.extraProducts.map((product) => convertLegacyBundleProduct(product, promotion)),
  ),
];

/**
 * If oldValue is null or undefined, takes value, otherwise uses oldValue
 */

const calculateDiscount = (bundle) =>
  update(bundle, {
    discount: {
      $set: bundle.products.reduce(
        (result, product) => result + product.price.oldValue - product.price.value,
        0,
      ),
    },
  });

const convertBundle = (promotion, recommendedTariff, data) => ({
  headline: (data.bundle || {}).headline,
  buttonText: (data.bundle || {}).buttonText,
  fullPriceText: (data.bundle || {}).fullPriceText,
  discountText: (data.bundle || {}).discountText,
  priceWithoutPromotion: promotion?.priceWithoutPromotion,
  discountPercent: promotion?.discountPercent,
  list: ((data.bundle || {}).list || [])
    .map((item) => {
      if (item.slug === BUNDLE_TYPE_PROMOTION) {
        const promotionProduct = (((data.bundle || {}).list || [])[0].products || [])[0] || {};
        item.products = composeBundleProducts(promotionProduct, promotion);
      }
      return item;
    })
    .map(calculateDiscount),
});

/**
 * TODO: Remove when SHOP-1892 is done
 */
const convertLegacyRecentlyViewedProduct = ({
  name,
  price,
  oldPrice,
  slug,
  id,
  image: { imageUrl },
  collectionId,
  collectionNameSingular,
  brandName,
  remain,
  isTariff,
}) => ({
  shopProductId: id,
  slug,
  category: collectionNameSingular,
  title: name,
  categoryId: collectionId,
  brand: brandName,
  imageUrl,
  remains: remain,
  isTariff,
  productUrl: `/shop/details/${slug}/`,
  price: {
    value: price,
    oldValue: oldPrice,
  },
});

const convertLegacyRecentlyViewedProductsList = ({
  headerText,
  products,
  buttonText,
  activeButtonText,
}) => ({
  headline: headerText || 'Вы недавно просматривали',
  list: products.map(convertLegacyRecentlyViewedProduct),
  recentNewShowIndex: 0,
  buttonText: buttonText || 'Купить',
  activeButtonText: activeButtonText || 'В корзине',
});

export const convertLegacyRecentlyViewedProducts = (data) =>
  (
    data.recentlyViewedProducts &&
    // if data is already remapped, use remapped data
    ((data.recentlyViewedProducts.headline && data.recentlyViewedProducts) ||
      // otherwise if product list is not empty, do conversion, or just return null
      data.recentlyViewedProducts.products.length)
  ) ?
    convertLegacyRecentlyViewedProductsList(data.recentlyViewedProducts)
  : null;

const getPromotion = (data) => (data.promotion && data.promotion.available) || {};

const convertSimilarProducts = (data = []) =>
  data.map((device) =>
    new DeviceCardBuilder()
      .setDeviceId(device)
      .setDevicePromotionId(device)
      .setDeviceHightlights(device)
      .setDeviceSlug(device)
      .setDeviceName(device)
      .setDeviceDiscount(device)
      .setDeviceLink(device)
      .setDeviceIsPreorder(device)
      .setDeviceIsOutOfStock(device)
      .setDeviceArticle(device)
      // SSR, basket, cmp, favorite saved in cookies
      // -----
      .setDeviceIsInBasket(device, [])
      .setDeviceIsFavorite(device, [])
      .setDeviceIsCmp(device, [])
      // -----
      .setDeviceImgOptions(device)
      .setDevicePromoBadges(device)
      .setDevicePrice(device)
      .setDeviceOldPrice(device)
      .getDeviceData(),
  );

export const getCrossSellCategorys = (list) => {
  let id = 0;
  const categorys = [{ id, text: 'Все', active: true }];
  const listCategorys = (list || []).map((item) => item.category);

  listCategorys.forEach((text) => {
    const findText = categorys.filter((item) => text === item.text);
    if (findText.length === 0) {
      id += 1;
      categorys.push({ id, text, active: false });
    }
  });

  return categorys;
};

const convertLegacyBody = ({ navigation, search = {}, ...data }) => ({
  isBestPricePromotion: !!data?.bestPricePromotion?.slug?.length,
  productId: data.productId,
  productRemains: data.productRemains || 0,
  title: data.title,
  metaTitle: data.metaTitle,
  baseUrl: data.baseUrl,
  absoluteBaseUrl: data.absoluteBaseUrl,
  images: data.images,
  breadcrumbs: data.breadcrumbs,
  price: data.price,
  impression: data.impression,
  bnpl: data.bnpl,
  description: data.description,
  seo: data.seo,
  isTariff: data.isTariff,
  dpcId: data.dpcId,
  badges: data.badges || [],
  highlightBadges: data.badges || [],
  forcePromotion:
    getPromotion(data).isOnlyWithPromo ?
      {
        id: getPromotion(data).id,
        slug: getPromotion(data).productWithPromotionSlug,
      }
    : null,
  isAuthorized: data.isAuthorized,
  isPreorder: data.isPreorder,
  isOutOfStock: data.isOutOfStock,
  outOfStockReasonSlug: data.outOfStockReasonSlug,
  outOfStockMessageTemplate: data.outOfStockMessageTemplate,
  requestUrl: data.requestUrl,
  article: data.article,
  outOfStock: data.outOfStock,
  showSaleDetails: data.showSaleDetails,
  brandName: data.isTariff ? TARIFF_BRAND_NAME : data.brandName,
  mainCollectionId: data.mainCollectionId,
  mainCollectionName: data.mainCollectionName,
  productName: data.productName,
  productSlug: data.productSlug,
  bonusService: data.bonusServices,
  contentPromotion: data.contentPromotion,
  metadata: data.metadata,
  navigation: {
    title: navigation.title,
    referenceTitle: navigation.referenceTitle,
    collections: navigation.collections,
    contacts: navigation.contacts,
  },
  feedback: {
    average: data.feedback.average,
    count: data.feedback.count,
    sortBy: FEEDBACK_SORT_BY_RATING /** constant equals 1 */,
    /**
     * TODO: remove hardcode when SHOP-839 is done
     */
    mostUseful: data.feedback.mostUseful && {
      ...data.feedback.mostUseful,
      title:
        (data.feedback.mostUseful || {}).title ||
        `Отзыв о ${((data.feedback.mostUseful || {}).product || {}).name || data.title}`,
    },
    list: data.feedback.list.map((post) => ({
      ...post,
      title: post.title || `Отзыв о ${post.product ? post.product.name : data.title}`,
    })),
    submitUrl: data.feedback.submitUrl,
    changeUsefulnessUrl: data.feedback.changeUsefulnessUrl,
    loadMoreUrl: data.feedback.loadMoreUrl,

    // TODO: remove when SHOP-1892 is done (validate that fields are properly removed)
    productsCookieName: null,
    userCookieName: null,

    ratedCookieName: data.feedback.ratedCookieName,

    /**
     * this cookie contains feedbacks in the following format:
     * 'likedId1,likedId2|dislikedId1,dislikedId2'
     * TODO: remove hardcode when SHOP-831 is done
     */
    newRatedCookieName: data.feedback.newRatedCookieName || 'beeline.shop.product.feedbacks.rated',
    maxCommentLength: data.feedback.maxCommentLength,
    submitSuccessTemplate: data.feedback.submitSuccessTemplate,
    submitErrorTemplate: data.feedback.submitErrorTemplate,
    agreement: data.feedback.agreement,
  },
  search: {
    placeholder: search.placeholder,
    url: search.url,
    suggestionsUrlTemplate: '/shop/searchsuggestion/shopsearch/?startswith={{query}}',
  },
  outofstockModal: {},
  characteristics: {
    brief: data.characteristics.brief,
    detailed: data.characteristics.detailed,
  },
  contentTabs: data.contentTabs,
  activeContentTab: data.activeContentTab || 'about',
  paymentDetails: data.paymentDetails || data.payment,
  services: data.services,
  creditDetails: data.creditDetails,
  leasingDetails: data.leasingDetails,
  deliveryDetails: data.deliveryDetails,
  delivery: data.delivery,
  crossSellProducts: {
    ...data.crossSellProducts,
    categorys: getCrossSellCategorys((data.crossSellProducts || {}).list || []),
  },

  // TODO: Временная заглушка
  // recentlyViewedProducts: convertLegacyRecentlyViewedProducts(data),
  recentlyViewedProducts: {
    isEnabled: true,
    url: '/shop/getproductdetailslastviewedproducts/',
    data: {
      product_id: 196759,
    },
    storage: {
      cookieName: 'beeshop_track_products',
      cookieDuration: 365,
      maxCount: 10,
    },
  },

  // TODO: Remove when SHOP-1823 is done
  bundles:
    data.bundles ||
    (data.promotion.available || data.recommendedTariff ?
      convertBundle(data.promotion.available, data.recommendedTariff, data)
    : null),
  colors: data.colors,
  activeColor: data.activeColor,
  types: data.types,
  tariffParams: data.tariffParams,
  preorderDetails: data.preorderDetails,
  promoBannerMarkup: data.promoBannerMarkup || null,
  shopCampaigns: data.shopCampaigns || [],
  promotion: data.promotion,
  esim: data.esim,
  officeBuy: data.officeBuy,
  partnerSlug: data.partnerSlug,
  similarProducts: {
    ...data.similarProducts,
    deviceList: convertSimilarProducts(data?.similarProducts?.list),
  },
});

export const convertLegacyModel = ({
  header,
  body,
  footer,
  settings: { common: settings, ...common },
}) => ({
  header: convertLegacyHeader(header),
  specialProjectHeader: convertHeader2024(header, common?.meta),
  body: convertLegacyBody(body),
  footer,
  common: {
    ...common,
    settings: {
      lastKnownResolutionCookieName: 'lastKnownResolution',
      compareUrl: '/shop/comparison/',
      compareCookieName: 'beeline.shop.products.comparison_98140',
      ...settings,
      lastKnownResolution: settings.lastKnownResolution ? Number(settings.lastKnownResolution) : '',
    },
  },
  settings: common,
});
